/**
 * kpop/event-calendar — year ribbon.
 *
 * The ribbon ships fully rendered and every month column is a real link to
 * ?kuukausi=YYYY-MM, so the calendar works without JS. This module upgrades it
 * to open the month in place, rendering rows from the JSON payload the server
 * embedded. The column set itself is never rebuilt here: which months the
 * window covers is the server's decision, and duplicating that rule in JS is
 * how the two drift apart.
 *
 * After rendering rows it fires `kpop:calendar-rendered`, which event-filters.js
 * listens for so an active category filter reaches the new rows too.
 */

const MONTHS_FULL = ['Tammikuu', 'Helmikuu', 'Maaliskuu', 'Huhtikuu', 'Toukokuu', 'Kesäkuu', 'Heinäkuu', 'Elokuu', 'Syyskuu', 'Lokakuu', 'Marraskuu', 'Joulukuu'];
const DAYS = ['Su', 'Ma', 'Ti', 'Ke', 'To', 'Pe', 'La'];

const esc = (value) =>
	String(value ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);

function eventRow(event) {
	const date = new Date(`${event.date}T12:00:00`);
	const place = [event.venue, event.city].filter(Boolean).join(' · ');
	const meta = [event.catName, place, event.time].filter(Boolean).join(' · ');

	return `
		<a class="cal-event" href="${esc(event.url)}" data-cat="${esc(event.cat)}" style="--cat: ${esc(event.color)}">
			<span class="cal-event__date"><b>${date.getDate()}.</b><span>${DAYS[date.getDay()]}</span></span>
			<span class="cal-event__body">
				<span class="cal-event__title">${esc(event.title)}</span>
				<span class="cal-event__meta"><i class="dot"></i>${esc(meta)}</span>
			</span>
		</a>`;
}

function initOne(root) {
	const payload = root.querySelector('.kpop-cal-data');
	const ribbon = root.querySelector('.cal-ribbon');
	const panel = root.querySelector('.cal-panel');
	const inner = root.querySelector('.cal-panel__inner');
	if (!payload || !ribbon || !panel || !inner) return;

	let events = [];
	try {
		events = JSON.parse(payload.textContent);
	} catch {
		return; // leave the server markup and its real links alone
	}

	const months = new Map();
	events.forEach((event) => {
		const key = event.date.slice(0, 7);
		if (!months.has(key)) months.set(key, []);
		months.get(key).push(event);
	});

	let openMonth = ribbon.querySelector('.rib.is-open')?.dataset.month || '';

	const paint = () => {
		ribbon.querySelectorAll('.rib[data-month]').forEach((column) => {
			const open = column.dataset.month === openMonth;
			column.classList.toggle('is-open', open);
			column.setAttribute('aria-expanded', String(open));
		});

		const items = openMonth ? months.get(openMonth) || [] : [];
		if (!items.length) {
			panel.classList.remove('is-open');
			inner.innerHTML = '';
			return;
		}

		const month = Number(openMonth.slice(5, 7));
		const count = items.length === 1 ? '1 tapahtuma' : `${items.length} tapahtumaa`;
		// Each month opens fresh: a month expanded earlier must not leave the
		// next one expanded before its button has been seen.
		inner.classList.remove('is-expanded');
		// The button is re-emitted because innerHTML discards the server's copy.
		// event-clamp.js hides it again when the month is short enough.
		inner.innerHTML =
			`<h3 class="cal-panel__title">${MONTHS_FULL[month - 1]} ${openMonth.slice(0, 4)} — ${count}</h3>` +
			items.map(eventRow).join('') +
			'<button type="button" class="ev-more-btn" data-ev-more data-label-more="Näytä lisää" hidden>Näytä lisää</button>';
		panel.classList.add('is-open');
	};

	ribbon.addEventListener('click', (e) => {
		const column = e.target.closest('.rib[data-month]');
		if (!column) return;
		e.preventDefault();
		openMonth = openMonth === column.dataset.month ? '' : column.dataset.month;
		paint();
		root.dispatchEvent(new CustomEvent('kpop:calendar-rendered', { bubbles: true }));
	});
}

export function initCalendar() {
	document.querySelectorAll('.kpop-cal').forEach(initOne);
}
